'use client'

import { getAuthorisedFileUrl } from '@/actions/marketplace'
import Error from '@/components/common/error'
import { getCurrencySymbol } from '@/helpers/currency'
import { downloadFile } from '@/helpers/downloads'
import { trackClick, trackEvent } from '@/lib/mixpanel'
import { cn } from '@/lib/utils'
import apiFetch from '@/store/helpers/api-fetch'
import { EmbeddedCheckoutProvider } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import Link from 'next/link'
import { usePathname, useSearchParams } from 'next/navigation'
import { useRouter } from 'nextjs-toploader/app'
import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import useSWRImmutable from 'swr/immutable'
import { TermsAlert } from '../liveries/terms-alert'
import StripeCheckoutModal from './stripe-checkout'

export default function LiveryBuyButton({ livery, className, session }) {
  const sessionUser = session?.user || null
  const pathname = usePathname()
  const [loading, setLoading] = useState(false)
  const [clientSecret, setClientSecret] = useState()
  const [userHasAgreedToTerms, setUserHasAgreedToTerms] = useState(
    sessionUser?.metadata?.termsAgreements?.liveryBuyer === true,
  )

  const searchParams = useSearchParams()
  const modalRef = useRef()
  const termsRef = useRef()
  const pendingDownloadRef = useRef(false)
  const router = useRouter()

  const price = livery.currentPrice ?? livery.price

  const isFreeLivery = price === '0.00' || price == 0

  const LIVERY_TERMS_COOKIE_PREFIX = 'livery_download_terms_accepted'
  const currentUserId = sessionUser?.id

  function getLiveryTermsCookieKey() {
    if (!currentUserId) return null
    return `${LIVERY_TERMS_COOKIE_PREFIX}_${currentUserId}`
  }

  function readLiveryTermsCookie() {
    const cookieKey = getLiveryTermsCookieKey()
    if (!cookieKey) return false
    if (typeof document === 'undefined') return false
    return document.cookie
      .split('; ')
      .some((cookie) => cookie.startsWith(`${cookieKey}=true`))
  }

  function writeLiveryTermsCookie() {
    const cookieKey = getLiveryTermsCookieKey()
    if (!cookieKey) return
    if (typeof document === 'undefined') return
    let cookie = `${cookieKey}=true; path=/; max-age=31536000; samesite=lax`
    if (window.location?.protocol === 'https:') {
      cookie += '; secure'
    }
    document.cookie = cookie
  }

  function hasAcceptedLiveryTerms() {
    return userHasAgreedToTerms || readLiveryTermsCookie()
  }

  const stripeAccount = livery.user.connectedAccounts?.find(
    (ca) => ca.serviceId === 'stripe',
  )

  const fullRoute =
    pathname + (searchParams.toString() ? '?' + searchParams.toString() : '')

  // If there is a stripe checkout ID in th query params
  // Fetch the checkout session (this button looks for checkout.status === 'complete' to signify payment success)
  const { data: checkoutStatus } = useSWRImmutable(
    searchParams.get('checkout') && stripeAccount
      ? {
          url: `marketplace/stripe-checkout/${stripeAccount.serviceAccountId}`,
          searchParams: {
            sessionId: searchParams.get('checkout'),
            itemId: livery.id,
          },
        }
      : null,
    apiFetch,
    { shouldRetryOnError: false },
  )

  // When checkout status chanegs to 'complete' and the itemId in the search params matches the livery id
  // Create a transaction log item for the user

  const completePurchase = useCallback(async () => {
    try {
      toast.loading('Completing purchase...', { id: 'complete-purchase' })

      await apiFetch({
        url: `livery-design/${livery.id}/transaction-log`,
        method: 'POST',
        data: { checkoutSessionId: searchParams.get('checkout') },
      })

      toast.success('Purchase completed', { id: 'complete-purchase' })

      const filteredSearchParams = Array.from(searchParams.entries())
        .filter(([key]) => key !== 'checkout')
        .map(([key, value]) => `${key}=${value}`)
        .join('&')

      const fullRouteWithoutCheckout =
        pathname + (filteredSearchParams ? '?' + filteredSearchParams : '')

      router.push(fullRouteWithoutCheckout)
    } catch (e) {
      console.error(e)
      toast.error('Error completing purchase', { id: 'complete-purchase' })
    }
  }, [livery.id, pathname, router, searchParams])

  useEffect(() => {
    if (
      checkoutStatus?.status === 'complete' &&
      searchParams.get('itemId') === livery.id
    ) {
      completePurchase()
    }
  }, [checkoutStatus?.status, completePurchase, livery.id, searchParams])

  async function getStripeInstance({ stripeAccountId }) {
    if (!stripeAccountId) return toast.error('Seller account not found')
    return await loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY, {
      stripeAccount: stripeAccountId,
    })
  }

  async function buyClickHandler() {
    trackEvent('Buy Button Clicked', { type: 'livery', livery })

    const res = await apiFetch({
      url: 'marketplace/stripe-checkout',
      method: 'POST',
      data: { itemId: livery.id },
    })

    const { client_secret } = res
    trackEvent('Livery Purchase', { type: 'livery', livery })
    setClientSecret(client_secret)
    modalRef.current.open()
  }

  async function downloadPurchasedLivery() {
    if (!session) {
      toast.error('You must be logged in to download this file')
      return
    }

    setLoading(true)

    try {
      await getAuthorisedFileUrl(sessionUser?.id, livery.id)
      await downloadLiveryFile()
    } catch (e) {
      console.error(e)
      toast.error(e.message || 'Cannot confirm purchase of livery')
    } finally {
      setLoading(false)
    }
  }

  async function handleFreeLiveryDownload() {
    await apiFetch({
      url: `livery-design/${livery.id}/transaction-log`,
      method: 'POST',
      data: {
        free: true,
      },
    })
    downloadLiveryFile()
  }

  async function performLiveryDownload() {
    try {
      const fileToDownload = livery.files.find(
        (file) => file.usageHint === 'user-livery-download',
      )

      if (!fileToDownload) {
        throw new Error('No download file found')
      }

      const originalFilename =
        fileToDownload.originalFilename || fileToDownload.name
      let downloadName = livery.name
      let downloadExt = 'zip'

      if (originalFilename) {
        const lastDotIndex = originalFilename.lastIndexOf('.')
        if (lastDotIndex > 0 && lastDotIndex < originalFilename.length - 1) {
          downloadName = originalFilename.slice(0, lastDotIndex)
          downloadExt = originalFilename.slice(lastDotIndex + 1)
        } else {
          downloadName = originalFilename
          if (downloadName.endsWith('.')) {
            downloadName = downloadName.slice(0, -1)
          }
        }
      }

      await downloadFile({
        s3Url: fileToDownload.fileUrl,
        name: downloadName,
        ext: downloadExt,
      })
      const useragent =
        typeof window !== 'undefined' ? navigator.userAgent : null

      const geolocation =
        typeof window !== 'undefined' ? navigator.geolocation : null

      const ipAddressRes = await fetch('https://api.ipify.org?format=json')
      const ipAddress = await ipAddressRes.json()

      trackEvent('Download Click', {
        type: 'livery',
        useragent,
        geolocation,
        ip: ipAddress.ip || 'UNKNOWN',
        metadata: {},
        livery: livery,
      })

      await apiFetch({
        url: 'livery-design/download-log',
        method: 'POST',
        data: {
          useragent,
          geolocation,
          ip: ipAddress.ip || 'UNKNOWN',
          metadata: {},
          userId: session.user.id,
          liveryDesignId: livery.id,
        },
      })
    } catch (e) {
      console.error(e)
      toast.error(e.message || 'Error downloading livery file')
    }
  }

  async function downloadLiveryFile() {
    if (!hasAcceptedLiveryTerms()) {
      pendingDownloadRef.current = true
      termsRef.current?.click()
      return
    }

    pendingDownloadRef.current = false
    await performLiveryDownload()
  }

  async function setTermsConfirm(bool) {
    if (bool === true) {
      writeLiveryTermsCookie()
      // Persist to DB in the background without blocking the download flow.
      apiFetch({
        url: `user/${sessionUser?.id}`,
        method: 'PATCH',
        data: {
          metadata: {
            ...sessionUser?.metadata,
            termsAgreements: { liveryBuyer: true },
          },
        },
      }).catch((e) => {
        console.error(e)
      })
    }
    setUserHasAgreedToTerms(bool)
    if (bool !== true) {
      pendingDownloadRef.current = false
      return
    }
    if (bool === true && pendingDownloadRef.current) {
      pendingDownloadRef.current = false
      await performLiveryDownload()
    }
  }

  if (!session) {
    return (
      <Link
        data-tracking-event={'Sign in button clicked'}
        href={`/auth/login?redirectUrl=${fullRoute}`}
        onClick={trackClick}
        className={cn(
          'inline-flex w-full animate-none items-center justify-center gap-2 rounded-sm border border-transparent bg-shades-0 px-4 py-2 text-center text-sm font-medium normal-case text-neutral-blue-900 transition-colors duration-200 hover:bg-shades-0/90 disabled:cursor-not-allowed disabled:opacity-60',
          className,
        )}
      >
        Sign in to {isFreeLivery ? 'download' : 'purchase'}
      </Link>
    )
  }

  if (!stripeAccount && !isFreeLivery) {
    return (
      <Error
        className="my-4 rounded bg-error text-white"
        error={{
          code: 'Error',
          message: 'Account error',
        }}
      />
    )
  }

  // IF transaction exists (session user ONLY), SHOW DOWNLOAD PURCHASED BUTTON REGARDLESS OF PRICE
  if (livery?.liveryTransactionLog?.length > 0) {
    return (
      <>
        <button
          className={cn(
            'inline-flex w-full animate-none items-center justify-center gap-2 rounded-sm border border-transparent bg-shades-0 px-4 py-2 text-center text-sm font-medium normal-case text-neutral-blue-900 transition-colors duration-200 hover:bg-shades-0/90 disabled:cursor-not-allowed disabled:opacity-60',
            className,
            { loading: loading },
          )}
          onClick={downloadPurchasedLivery}
        >
          Download
        </button>
        <div className="absolute">
          <TermsAlert setter={setTermsConfirm} reference={termsRef} />
        </div>
      </>
    )
  }

  // IF NOT PURCHASED
  // IF FREE, SHOW FREE DOWNLOAD BUTTON
  if (isFreeLivery) {
    if (
      livery.files.filter((file) => file.usageHint === 'user-livery-download')
        .length > 0
    ) {
      return (
        <>
          <button
            className={cn(
              'inline-flex w-full animate-none items-center justify-center gap-2 rounded-sm border border-transparent bg-shades-0 px-4 py-2 text-center text-sm font-medium normal-case text-neutral-blue-900 transition-colors duration-200 hover:bg-shades-0/80 disabled:cursor-not-allowed disabled:opacity-60',
              className,
            )}
            onClick={handleFreeLiveryDownload}
          >
            Download
          </button>
          <div className="absolute">
            <TermsAlert setter={setTermsConfirm} reference={termsRef} />
          </div>
        </>
      )
    }
  } else if (stripeAccount.serviceAccountId) {
    // IF PAID
    // CHECK FOR VALID SELLER STRIPE ACOUNT CONNECTION
    return (
      <>
        <button
          className={cn(
            'inline-flex w-full animate-none items-center justify-center gap-2 rounded-sm border border-transparent bg-success px-4 py-2 text-center text-sm font-medium normal-case text-neutral-blue-900 transition-colors duration-200 hover:bg-success/90 disabled:cursor-not-allowed disabled:opacity-60',
            className,
          )}
          onClick={buyClickHandler}
        >
          Buy now {`${getCurrencySymbol(livery.currencyId)}${price}`}
        </button>

        <EmbeddedCheckoutProvider
          stripe={getStripeInstance({
            stripeAccountId: stripeAccount.serviceAccountId,
          })}
          options={{
            clientSecret,
            onComplete: () => {},
          }}
        >
          <StripeCheckoutModal ref={modalRef} />
        </EmbeddedCheckoutProvider>

        <div className="absolute">
          <TermsAlert setter={setTermsConfirm} reference={termsRef} />
        </div>
      </>
    )
  } else {
    return (
      <button
        className={cn(
          'pointer-events-none inline-flex w-full animate-none items-center justify-center gap-2 rounded-sm border border-transparent bg-neutral-blue-700 px-4 py-2 text-center text-sm font-medium normal-case text-error transition-colors duration-200 hover:bg-neutral-blue-700/80 disabled:cursor-not-allowed disabled:opacity-60',
          className,
        )}
      >
        Not enabled
      </button>
    )
  }
}
